"use client";
import { Track } from "@/src/lib/tracking";

import React, { useEffect, useRef, useState } from "react";

import { cn } from "utils";

interface CountdownCardProps {
  value: string;
  label?: string;
  className?: string;
  containerClassName?: string;
  textClassName?: string;
  countDownClassName?: string;
}

// 时间单位组件 - 包含十位和个位，使用两个 ref 分别记录
export const CountdownCard: React.FC<CountdownCardProps> = ({
  value,
  label: _label,
  className,
  countDownClassName,
  containerClassName,
  textClassName,
}) => {
  const tensDigit = value.charAt(0); // 十位数
  const onesDigit = value.charAt(1); // 个位数

  // 十位数状态
  const [tensCurrentValue, setTensCurrentValue] = useState(tensDigit);
  const [tensNextValue, setTensNextValue] = useState(tensDigit);
  const [tensAnimating, setTensAnimating] = useState(false);
  const tensPrevValueRef = useRef<string | null>(null);

  // 个位数状态
  const [onesCurrentValue, setOnesCurrentValue] = useState(onesDigit);
  const [onesNextValue, setOnesNextValue] = useState(onesDigit);
  const [onesAnimating, setOnesAnimating] = useState(false);
  const onesPrevValueRef = useRef<string | null>(null);

  // 监听十位数变化
  useEffect(() => {
    Track.log("info", "CountdownCard", "TensDigit value changed", {
      value: tensDigit,
      prevValue: tensPrevValueRef.current,
      isAnimating: tensAnimating,
      valueChanged: tensDigit !== tensPrevValueRef.current,
      isFirstRender: tensPrevValueRef.current === null,
    });

    // 检查十位数是否真正发生了变化（排除首次渲染）
    if (
      tensPrevValueRef.current !== null &&
      tensDigit !== tensPrevValueRef.current
    ) {
      Track.log("info", "CountdownCard", "TensDigit animation starting", {
        value: tensDigit,
        prevValue: tensPrevValueRef.current,
      });
      setTensNextValue(tensDigit);
      setTensAnimating(true);

      // 动画结束后更新当前值并重置状态
      const timer = setTimeout(() => {
        setTensCurrentValue(tensDigit);
        setTensAnimating(false);
        Track.log("info", "CountdownCard", "TensDigit animation completed", {
          value: tensDigit,
        });
      }, 350); // 动画时长 350ms

      // 更新上一次的值
      tensPrevValueRef.current = tensDigit;

      return () => clearTimeout(timer);
    } else {
      Track.log(
        "info",
        "CountdownCard",
        "TensDigit values are equal or first render, no animation needed",
        {
          value: tensDigit,
          prevValue: tensPrevValueRef.current,
          isFirstRender: tensPrevValueRef.current === null,
        },
      );
      // 更新上一次的值
      tensPrevValueRef.current = tensDigit;
    }
  }, [tensDigit]); // eslint-disable-line react-hooks/exhaustive-deps

  // 监听个位数变化
  useEffect(() => {
    Track.log("info", "CountdownCard", "OnesDigit value changed", {
      value: onesDigit,
      prevValue: onesPrevValueRef.current,
      isAnimating: onesAnimating,
      valueChanged: onesDigit !== onesPrevValueRef.current,
      isFirstRender: onesPrevValueRef.current === null,
    });

    // 检查个位数是否真正发生了变化（排除首次渲染）
    if (
      onesPrevValueRef.current !== null &&
      onesDigit !== onesPrevValueRef.current
    ) {
      Track.log("info", "CountdownCard", "OnesDigit animation starting", {
        value: onesDigit,
        prevValue: onesPrevValueRef.current,
      });
      setOnesNextValue(onesDigit);
      setOnesAnimating(true);

      // 动画结束后更新当前值并重置状态
      const timer = setTimeout(() => {
        setOnesCurrentValue(onesDigit);
        setOnesAnimating(false);
        Track.log("info", "CountdownCard", "OnesDigit animation completed", {
          value: onesDigit,
        });
      }, 350); // 动画时长 350ms

      // 更新上一次的值
      onesPrevValueRef.current = onesDigit;

      return () => clearTimeout(timer);
    } else {
      Track.log(
        "info",
        "CountdownCard",
        "OnesDigit values are equal or first render, no animation needed",
        {
          value: onesDigit,
          prevValue: onesPrevValueRef.current,
          isFirstRender: onesPrevValueRef.current === null,
        },
      );
      // 更新上一次的值
      onesPrevValueRef.current = onesDigit;
    }
  }, [onesDigit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cn(
        "relative bg-content_primary rounded-1 md:rounded-lg flex flex-col items-center justify-center overflow-hidden",
        className,
      )}
    >
      <div
        className={cn(
          "relative w-7 h-7 md:w-14 md:h-14 flex items-center justify-center overflow-hidden",
          countDownClassName,
        )}
      >
        {/* 十位数 - 使用两个 span 切换 */}
        <div
          className={cn(
            "absolute left-0.8 inset-y-0 w-[11px] md:w-7 flex items-center justify-center overflow-hidden",
            containerClassName,
          )}
        >
          {/* 当前显示的数字 */}
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center body_bold_m md:headline_regular_l text-content_inverse_primary",
              textClassName,
            )}
            style={{
              transform: tensAnimating ? "translateY(100%)" : "translateY(0%)",
              transition: tensAnimating
                ? "transform 350ms ease-in-out"
                : "none",
            }}
          >
            {tensCurrentValue}
          </span>
          {/* 新数字 */}
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center body_bold_m md:headline_regular_l text-content_inverse_primary",
              textClassName,
            )}
            style={{
              transform: tensAnimating ? "translateY(0%)" : "translateY(-100%)",
              transition: tensAnimating
                ? "transform 350ms ease-in-out"
                : "none",
            }}
          >
            {tensNextValue}
          </span>
        </div>

        {/* 个位数 - 使用两个 span 切换 */}
        <div
          className={cn(
            "absolute right-1.2 inset-y-0 w-[11px] md:w-7 flex items-center justify-center overflow-hidden",
            containerClassName,
          )}
        >
          {/* 当前显示的数字 */}
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center body_bold_m md:headline_regular_l text-content_inverse_primary",
              textClassName,
            )}
            style={{
              transform: onesAnimating ? "translateY(100%)" : "translateY(0%)",
              transition: onesAnimating
                ? "transform 350ms ease-in-out"
                : "none",
            }}
          >
            {onesCurrentValue}
          </span>
          {/* 新数字 */}
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center body_bold_m md:headline_regular_l text-content_inverse_primary",
              textClassName,
            )}
            style={{
              transform: onesAnimating ? "translateY(0%)" : "translateY(-100%)",
              transition: onesAnimating
                ? "transform 350ms ease-in-out"
                : "none",
            }}
          >
            {onesNextValue}
          </span>
        </div>
      </div>
      {_label && (
        <div className="body_s text-content_quaternary mb-1">{_label}</div>
      )}
    </div>
  );
};
